// src/modules/gyms/hooks/useGymBrands.ts
import {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { BrandDto } from "@modules/brands/models/brand.model";
import { BrandService } from "@modules/brands/services/brand.services";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";

export function useGymBrands() {
  const location = useLocation();
  const isGymRoute = location.pathname.startsWith("/b2b/gyms");

  const { auth, setAuth, logout } = useContext(AuthContext);
  const service = useMemo(() => new BrandService(), []);
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);

  const load = useCallback(async () => {
    if (!auth || !isGymRoute) {
      if (!auth) setLoaded(true);
      return;
    }
    if (loadingRef.current || initialLoadDone.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await service.getAllBrands({ auth, setAuth, logout }, "gym");
      setBrands(data);
    } catch (err: any) {
      setError(err.message || "Không thể tải danh sách thương hiệu gym");
    } finally {
      setLoading(false);
      setLoaded(true);
      loadingRef.current = false;
      initialLoadDone.current = true;
    }
  }, [auth, setAuth, logout, service, isGymRoute]);

  useEffect(() => {
    if (!isGymRoute) {
      setBrands([]);
      setLoaded(true);
      setError(null);
      initialLoadDone.current = false;
      loadingRef.current = false;
      return;
    }
    if (!initialLoadDone.current) {
      load();
    }
  }, [isGymRoute, load]);

  const refresh = useCallback(async () => {
    initialLoadDone.current = false;
    await load();
  }, [load]);

  return { brands, loading, loaded, error, refresh };
}
